import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#2D5A27", // Deep forest green
      light: "#4A7C59",
      dark: "#1E3A1A",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#E8B4A0", // Soft terracotta
      light: "#F2D4C7",
      dark: "#D19A7A",
      contrastText: "#2D2D2D",
    },
    background: {
      default: "#FAFAFA", // Clean off-white
      paper: "#FFFFFF",
    },
    text: {
      primary: "#2D2D2D", // Deep charcoal
      secondary: "#6B7280", // Muted gray
    },
    success: {
      main: "#10B981", // Emerald green
      light: "#34D399",
      dark: "#059669",
    },
    warning: {
      main: "#F59E0B", // Amber
      light: "#FBBF24",
      dark: "#D97706",
    },
    error: {
      main: "#EF4444", // Red
      light: "#F87171",
      dark: "#DC2626",
    },
  },
  typography: {
    fontFamily: [
      "HanaMinA",
      "花園明朝",
      "Inter",
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      "sans-serif",
    ].join(","),
    h1: {
      fontWeight: 700,
      fontSize: "3.5rem",
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
      color: "#2D2D2D",
    },
    h2: {
      fontWeight: 600,
      fontSize: "2.5rem",
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
      color: "#2D2D2D",
    },
    h3: {
      fontWeight: 600,
      fontSize: "2rem",
      lineHeight: 1.4,
      color: "#2D2D2D",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.4,
      color: "#2D2D2D",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.4,
      color: "#2D2D2D",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: 1.4,
      color: "#2D2D2D",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.7,
      color: "#4B5563",
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.6,
      color: "#6B7280",
    },
    button: {
      fontWeight: 600,
      textTransform: "none",
      fontSize: "0.875rem",
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow:
            "0px 4px 6px rgba(0, 0, 0, 0.07), 0px 2px 4px rgba(0, 0, 0, 0.06)",
          borderRadius: 20,
          border: "1px solid rgba(0, 0, 0, 0.05)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          "&:hover": {
            boxShadow:
              "0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)",
            transform: "translateY(-4px)",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 12,
          fontWeight: 600,
          padding: "12px 24px",
          fontSize: "0.875rem",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow:
              "0px 4px 6px rgba(0, 0, 0, 0.07), 0px 2px 4px rgba(0, 0, 0, 0.06)",
          },
        },
        contained: {
          backgroundColor: "#2D5A27",
          color: "#FFFFFF",
          "&:hover": {
            backgroundColor: "#1E3A1A",
            boxShadow: "0px 10px 15px rgba(45, 90, 39, 0.3)",
          },
        },
        outlined: {
          borderColor: "#2D5A27",
          color: "#2D5A27",
          borderWidth: "2px",
          "&:hover": {
            backgroundColor: "rgba(45, 90, 39, 0.08)",
            borderColor: "#1E3A1A",
          },
        },
        text: {
          color: "#2D5A27",
          "&:hover": {
            backgroundColor: "rgba(45, 90, 39, 0.08)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow:
            "0px 4px 6px rgba(0, 0, 0, 0.07), 0px 2px 4px rgba(0, 0, 0, 0.06)",
        },
        elevation1: {
          boxShadow:
            "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            "& fieldset": {
              borderColor: "rgba(0, 0, 0, 0.1)",
              borderWidth: "1px",
            },
            "&:hover fieldset": {
              borderColor: "rgba(45, 90, 39, 0.3)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#2D5A27",
              borderWidth: "2px",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#6B7280",
            "&.Mui-focused": {
              color: "#2D5A27",
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          fontWeight: 500,
          fontSize: "0.75rem",
        },
        filled: {
          backgroundColor: "rgba(45, 90, 39, 0.1)",
          color: "#2D5A27",
          "&:hover": {
            backgroundColor: "rgba(45, 90, 39, 0.15)",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          boxShadow:
            "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: "0px 25px 50px rgba(0, 0, 0, 0.15)",
        },
      },
    },
    MuiCardMedia: {
      styleOverrides: {
        root: {
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      },
    },
  },
});

export default theme;
